/**
 * PointsCelebrationProvider — global "+N points" feedback bus.
 * Mounted once near the app root. Listens for `points-awarded` CustomEvents
 * and shows a centered FloatingPoints burst plus a sonner toast.
 */
import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { FloatingPoints } from "./FloatingPoints";

interface PointsAwardedDetail {
  amount: number;
  reason?: string;
  milestone?: boolean;
}
interface QueueItem extends PointsAwardedDetail {
  id: number;
}

export function PointsCelebrationProvider() {
  const [current, setCurrent] = useState<QueueItem | null>(null);
  const queueRef = useRef<QueueItem[]>([]);
  const idRef = useRef(0);
  const showingRef = useRef(false);

  useEffect(() => {
    const showNext = () => {
      if (showingRef.current) return;
      const next = queueRef.current.shift();
      if (!next) return;
      showingRef.current = true;
      setCurrent(next);
    };

    const handler = (e: Event) => {
      const detail = (e as CustomEvent<PointsAwardedDetail>).detail;
      if (!detail || !Number.isFinite(detail.amount) || detail.amount <= 0) return;
      const item: QueueItem = { ...detail, id: ++idRef.current };
      queueRef.current.push(item);
      toast.success(`+${detail.amount} points`, {
        description: detail.reason || "Nice work — keep it going.",
        duration: 2500,
      });
      showNext();
    };

    window.addEventListener("points-awarded", handler);
    return () => window.removeEventListener("points-awarded", handler);
  }, []);

  const handleComplete = () => {
    showingRef.current = false;
    setCurrent(null);
    setTimeout(() => {
      const next = queueRef.current.shift();
      if (next) {
        showingRef.current = true;
        setCurrent(next);
      }
    }, 150);
  };

  return (
    <FloatingPoints
      key={current?.id ?? "idle"}
      points={current?.amount ?? 0}
      show={!!current}
      onComplete={handleComplete}
    />
  );
}
